import { FC, useEffect, useRef, useState } from 'react';
import { useGenerateKidComparison } from '../../../../../api/kids/hooks/mutations';
import { toast } from '../../../../toast/Toast';
import JSZip from 'jszip';
import TokenService from '../../../../../services/token-service';
import { useGetKidCompareData } from '../../../../../api/kids/hooks/queries';
import RaptorPDFViewer from '../../../../general/raptorPDFViewer/RaptorPDFViewer';
import { pdfjs } from 'react-pdf';
import { getOS } from '../../../../../utilities/helpers/common/system';
import { useFundAndShareClassName } from '../hooks/useFundAndShareClassName';
import useRouteParams from '../../../../../hooks/useRouteParams';
import { useSynchronizedPdfScroll } from '../hooks/useSynchronizedPdfScroll';
import ThresholdSelector, {
  ComparisonThreshold,
} from '../components/ThresholdSelector';
import { transformKidDataModelsToElements } from '../utils/kidDataTransformers';

export interface KidsComparisonViewProps {
  /** Optional content to render on the right side of the toolbar (e.g. Accept/Reject buttons on review page) */
  toolbarRight?: React.ReactNode;
  /** Page key for useFundAndShareClassName (e.g. 'kid-review-v2', 'kid-compare-v2') */
  pageKey: string;
  /** Client name for the comparison API payload (e.g. from clientNameSelector) */
  clientName: string;
}

/**
 * Shared layout and logic for the KID review and compare pages:
 * toolbar with "Compare by", "Link scroll", and optional right content,
 * plus two side-by-side PDF viewers with synchronized scrolling.
 */
const KidsComparisonView: FC<KidsComparisonViewProps> = ({
  toolbarRight,
  pageKey,
  clientName: clientNameProp,
}) => {
  const { user_client_id: clientId } = TokenService.getUser()!;
  const OS = getOS();

  const [threshold, setThreshold] = useState<ComparisonThreshold>('word');
  const [scrollLinked, setScrollLinked] = useState(true);

  const fundId = useRouteParams('fundId')['fundId'] || '';
  const shareClassId = useRouteParams('shareClassId')['shareClassId'] || '';
  const documentLanguage =
    useRouteParams('documentLanguage')['documentLanguage'] || 'English';
  const compareVersion =
    useRouteParams('compareVersion')['compareVersion'] || '';
  const targetVersion = useRouteParams('targetVersion')['targetVersion'] || '';

  const [targetPdfUrl, setTargetPdfUrl] = useState<string | null>(null);
  const [comparePdfUrl, setComparePdfUrl] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [targetHasError, setTargetHasError] = useState(false);
  const [compareHasError, setCompareHasError] = useState(false);

  const urlsRef = useRef<{ target: string | null; compare: string | null }>({ target: null, compare: null });
  urlsRef.current = { target: targetPdfUrl, compare: comparePdfUrl };

  const {
    targetPdfRef,
    comparePdfRef,
    handleTargetScroll,
    handleCompareScroll,
  } = useSynchronizedPdfScroll();

  const generateKidComparisonMutation = useGenerateKidComparison();

  const {
    fundName,
    fundNameFull,
    shareClassName,
    shareClassIsin,
    areNamesReady,
  } = useFundAndShareClassName({
    clientId,
    fundId,
    shareClassId,
    pageKey,
  });

  const {
    data: compareData,
    isLoading: isLoadingCompareData,
  } = useGetKidCompareData(
    clientId,
    fundId,
    shareClassId,
    compareVersion,
    targetVersion,
    documentLanguage,
  );

  useEffect(() => {
    if (OS !== 'linux') {
      pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
    }
  }, [OS]);

  useEffect(() => {
    if (!compareData || isLoadingCompareData || !areNamesReady) return;

    const generatePdfs = async () => {
      try {
        setErrorMessage('');

        const response = await generateKidComparisonMutation.mutateAsync({
          client: clientNameProp || '',
          fund_name: fundName,
          fund_name_full: fundNameFull,
          share_class: shareClassName,
          threshold,
          comparison_version: transformKidDataModelsToElements(compareData.comparison_version),
          target_version: transformKidDataModelsToElements(compareData.target_version),
        });

        const zipBlob = response.data;
        const zip = await JSZip.loadAsync(zipBlob);
        const fileNames = Object.keys(zip.files).filter(
          (name) => name.endsWith('.pdf') && !zip.files[name].dir,
        );

        if (fileNames.length < 2) {
          throw new Error('ZIP file must contain at least 2 PDF files');
        }

        const targetPdfBlob = await zip.files[fileNames[0]].async('blob');
        const comparePdfBlob = await zip.files[fileNames[1]].async('blob');

        const targetUrl = window.URL.createObjectURL(
          new Blob([targetPdfBlob], { type: 'application/pdf' }),
        );
        const compareUrl = window.URL.createObjectURL(
          new Blob([comparePdfBlob], { type: 'application/pdf' }),
        );

        setTargetPdfUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return targetUrl;
        });
        setComparePdfUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return compareUrl;
        });
      } catch (error) {
        const message = (error as Error)?.message || 'Failed to load comparison PDFs';
        setErrorMessage(message);
        toast({
          title: 'Failed to load comparison',
          message: (error as Error)?.message || 'Unknown error',
          type: 'error',
        });
      }
    };

    generatePdfs();
  }, [
    compareData,
    threshold,
    areNamesReady,
    fundName,
    fundNameFull,
    shareClassName,
    clientNameProp,
  ]);

  useEffect(() => {
    return () => {
      const { target, compare } = urlsRef.current;
      if (target) URL.revokeObjectURL(target);
      if (compare) URL.revokeObjectURL(compare);
    };
  }, []);

  return (
    <div className="flex h-[calc(100vh-8.75rem)] w-full flex-col gap-2">
      <div className="flex flex-shrink-0 items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-2 shadow-sm">
        <div className="flex items-center gap-6">
          <ThresholdSelector value={threshold} onChange={setThreshold} />
          <label
            className="flex cursor-pointer items-center gap-2"
            title={scrollLinked ? 'Scroll positions are linked' : 'Scroll independently'}
          >
            <input
              type="checkbox"
              checked={scrollLinked}
              onChange={(e) => setScrollLinked(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-gray-300 text-mainblue focus:ring-mainblue"
            />
            <span className="text-xs font-medium text-gray-700">Link scroll</span>
          </label>
        </div>
        {toolbarRight}
      </div>

      <div className="flex min-h-0 flex-1 gap-2">
        <RaptorPDFViewer
          ref={targetPdfRef}
          pdfUrl={targetPdfUrl}
          fileName={`${fundNameFull || fundName} - ${shareClassIsin || shareClassId} - v${targetVersion}`}
          hasError={targetHasError}
          errorMessage={errorMessage || 'Failed to load PDF'}
          setHasError={setTargetHasError}
          isPending={generateKidComparisonMutation.isPending}
          onScroll={scrollLinked ? handleTargetScroll : undefined}
          className="h-full"
          customToolbarComponents={
            <span className="mr-2 text-sm font-semibold text-gray-600">
              Target (v{targetVersion || 'target'})
            </span>
          }
        />

        <RaptorPDFViewer
          ref={comparePdfRef}
          pdfUrl={comparePdfUrl}
          fileName={`${fundNameFull || fundName} - ${shareClassIsin || shareClassId} - v${compareVersion}`}
          hasError={compareHasError}
          errorMessage={errorMessage || 'Failed to load PDF'}
          setHasError={setCompareHasError}
          isPending={generateKidComparisonMutation.isPending}
          onScroll={scrollLinked ? handleCompareScroll : undefined}
          className="h-full"
          customToolbarComponents={
            <span className="mr-2 text-sm font-semibold text-gray-600">
              Comparison (v{compareVersion || 'compare'})
            </span>
          }
        />
      </div>
    </div>
  );
};

export default KidsComparisonView;
